import moment from 'moment';
import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { AllFund } from '@/api/fund/queries';
import { FundApprovalStatus, fundApprovalStatus } from '@/api/fund/types';
import DefaultCover from '@/assets/png/fund-dashboard/hands.png';
import ManageIcon from '@/assets/svg/ui-elements/arrow-right.svg';
import TickIcon from '@/assets/svg/ui-elements/check-circle.svg';
import RejectIcon from '@/assets/svg/ui-elements/close.svg';
import TrashIcon from '@/assets/svg/ui-elements/delete-filled.svg';
import LockIcon from '@/assets/svg/ui-elements/lock-red.svg';
import RestoreIcon from '@/assets/svg/ui-elements/refresh.svg';
import SmsIcon from '@/assets/svg/ui-elements/sms-filled-white.svg';
import UnblockIcon from '@/assets/svg/ui-elements/unlock.svg';
import { normalizeIAStatus } from '@/pages/fund-dashboard/modules/fund-dashboard-card';
import {
  CoverImage,
  Divider,
  EntityNameText,
  HeaderRow,
  HeaderTextCol,
  MetricLabel,
  MetricRow,
  MetricsCol,
  MetricValue,
  NameText,
  StatusChip,
  StatusRow,
} from '@/pages/fund-dashboard/modules/fund-dashboard-card.styled';
import { getSetupFundReviewOrEditPath } from '@/pages/setup-fund/utils/fund-review-routing';
import { PagePaths } from '@/routes/page-paths';
import { CustomButton } from '@/shared/components/custom-button';
import { SuspendedFundAlert } from '@/shared/components/suspended-fund-alert';
import { Tooltip } from '@/shared/design-system';
import { colors } from '@/shared/design-system/colors';
import Badge from '@/shared/design-system/components/Badge';
import { BadgeColor } from '@/shared/design-system/components/Badge/types';
import { Permission, useCan } from '@/shared/modules/permissions';
import { statusVisual } from '@/utils/fund-status-visual';

import { ActionRow, ActionsCol, GalleryItemContainer } from './styled';
import { useFundAdminActions } from './use-fund-admin-actions';

type VerificationBadge = { label: string; color: BadgeColor; tooltip: string };

const verificationBadge: Record<FundApprovalStatus, VerificationBadge> = {
  [fundApprovalStatus.Draft]: {
    label: 'Draft',
    color: 'default',
    tooltip: 'This is a draft which has not yet been submitted for approval.',
  },
  [fundApprovalStatus.Verified]: {
    label: 'Verified',
    color: 'primary',
    tooltip: 'This Fund has been approved by Gamma Prime.',
  },
  [fundApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    color: 'orange',
    tooltip:
      'This Fund was previously verified, and it is currently waiting for a new approval from Gamma Prime after key information was edited.',
  },
  [fundApprovalStatus.AwaitingApproval]: {
    label: 'Awaiting Approval',
    color: 'yellow',
    tooltip: 'This Fund has been newly created and is waiting for approval from Gamma Prime.',
  },
  [fundApprovalStatus.Rejected]: {
    label: 'Rejected',
    color: 'destructive',
    tooltip: 'This Fund was reviewed and rejected by Gamma Prime.',
  },
  [fundApprovalStatus.Blocked]: {
    label: 'Blocked',
    color: 'destructive',
    tooltip: 'This verified Fund has been blocked by Gamma Prime.',
  },
};

const stop = (e: MouseEvent) => e.stopPropagation();

// ─── Legacy export preserved for consumers in _id pages, list view, and entities tab ─
type FundApprovalStatusStyles = Record<
  FundApprovalStatus,
  { label: string; bg: string; color: string; info: string }
>;

export const fundApprovalStatusStyles: FundApprovalStatusStyles = {
  [fundApprovalStatus.Draft]: {
    label: 'Draft',
    bg: '#BDBEC21A',
    color: '#BDBEC2',
    info: 'This is a draft which has not yet been submitted for approval.',
  },
  [fundApprovalStatus.Verified]: {
    label: 'Verified',
    bg: '#39CA8840',
    color: '#39CA88',
    info: 'This Fund has been approved by Gamma Prime.',
  },
  [fundApprovalStatus.ReVerificationPending]: {
    label: 'Re-verification Pending',
    bg: '#FFAA5A40',
    color: '#FFAA5A',
    info: 'This Fund was previously verified, and it is currently waiting for a new approval from Gamma Prime after key information was edited.',
  },
  [fundApprovalStatus.AwaitingApproval]: {
    label: 'Awaiting Approval',
    bg: '#40462C',
    color: '#F9D151',
    info: 'This Fund has been newly created and is waiting for approval from Gamma Prime.',
  },
  [fundApprovalStatus.Rejected]: {
    label: 'Rejected',
    bg: '#E6483D1A',
    color: '#E6483D',
    info: 'This Fund was reviewed and rejected by Gamma Prime.',
  },
  [fundApprovalStatus.Blocked]: {
    label: 'Blocked',
    bg: '#E6483D1A',
    color: '#E6483D',
    info: 'This verified Fund has been blocked by Gamma Prime.',
  },
};

export const GalleryFundTableItem = ({ item }: { item: AllFund }) => {
  const canReviewCrossEntity = useCan(Permission.GlobalAdminCrossEntityAccess);
  const canReview = useCan(Permission.FundReview);
  const canDelete = useCan(Permission.FundDelete);
  const reviewDeniedReason = canReview ? '' : 'You do not have permission to review this fund';
  const deleteDeniedReason = canDelete ? '' : 'You do not have permission to delete this fund';
  const navigate = useNavigate();

  const iaKey = normalizeIAStatus(item?.investmentAvailabilityStatus);
  const visual = statusVisual[iaKey] ?? statusVisual.None;
  const StatusIcon = visual.icon;

  const badge = verificationBadge[item.fundApprovalStatus];
  // badge.tooltip / buildBlockedTooltip is intentionally unused for now — the new Badge
  // doesn't expose a title prop; revisit if hover-tooltip is required.

  const aum = item?.sizeInMillionsUsd
    ? `$${(item.sizeInMillionsUsd * 1_000_000).toLocaleString('en-US')}`
    : '$0';
  const lastUpdated = item.updatedAt ? moment(item.updatedAt).format('MMM D, YYYY') : '—';

  const coverSrc = item.cardImageUrl || DefaultCover;
  const name = item.name || '';
  const entityName = item.entityName || '';

  const { show, handlers } = useFundAdminActions({
    id: item?.id,
    name: item?.name,
    entityId: item.entity.id,
    fundApprovalStatus: item.fundApprovalStatus,
    entityApproved: item?.entity.isEntityApproved,
  });

  const handleCardClick = () => {
    navigate(PagePaths.overviewFund.replace(':id', item.id));
  };

  const handleReview = (e: MouseEvent) => {
    e.stopPropagation();
    navigate(
      getSetupFundReviewOrEditPath({
        id: item.id,
        canReviewCrossEntity,
        fundApprovalStatus: item.fundApprovalStatus,
        draftStatus: item.draftStatus,
      }),
    );
  };

  const topRowVisible = show.approve || show.reject || show.restore || show.unblock || show.edit;
  const bottomRowVisible = show.contact || show.block || show.delete;

  return (
    <GalleryItemContainer onClick={handleCardClick}>
      <HeaderRow>
        <CoverImage $src={coverSrc} />
        <HeaderTextCol>
          <NameText>{name}</NameText>
          {entityName && <EntityNameText>{entityName}</EntityNameText>}
        </HeaderTextCol>
      </HeaderRow>

      <Divider />

      <StatusRow>
        <StatusChip $color={visual.color}>
          {StatusIcon && <StatusIcon width={14} height={14} />}
          {visual.label}
        </StatusChip>
        <Badge label={badge.label} color={badge.color} variant="solid" />
        <SuspendedFundAlert
          fundApprovalStatus={item.fundApprovalStatus}
          entityApprovalStatus={item.entity?.entityApprovalStatus}
        />
      </StatusRow>

      <MetricsCol>
        <MetricRow>
          <MetricLabel>Units (Total)</MetricLabel>
          <MetricValue>{(item.unitsIssued ?? 0).toLocaleString('en-US')}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Fund AUM</MetricLabel>
          <MetricValue>{aum}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Investors</MetricLabel>
          <MetricValue>{item.investors ?? 0}</MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>Last Updated</MetricLabel>
          <MetricValue>{lastUpdated}</MetricValue>
        </MetricRow>
      </MetricsCol>

      <Divider />

      <ActionsCol onClick={stop}>
        {topRowVisible && (
          <ActionRow>
            {show.approve && (
              <Tooltip
                title={
                  reviewDeniedReason ||
                  'This Fund cannot be approved until its parent Entity is approved.'
                }
                disabled={canReview && !!item.entity.isEntityApproved}
              >
                <span>
                  <CustomButton
                    fullWidth
                    disabled={!canReview || !item.entity.isEntityApproved}
                    onClick={handlers.approve}
                    startIcon={<TickIcon width={16} height={16} color="white" />}
                    sx={{
                      height: 40,
                      borderRadius: '8px',
                      fontWeight: 500,
                      background: '#39CA88',
                      '&:hover': { background: '#39CA88', opacity: 0.9 },
                    }}
                  >
                    Approve
                  </CustomButton>
                </span>
              </Tooltip>
            )}

            {show.reject && (
              <Tooltip title={reviewDeniedReason}>
                <span>
                  <CustomButton
                    fullWidth
                    disabled={!canReview}
                    onClick={handlers.openRejectModal}
                    startIcon={<RejectIcon width={16} height={16} />}
                    sx={{
                      height: 40,
                      borderRadius: '8px',
                      fontWeight: 500,
                      background: colors.error.solid,
                      '&:hover': { background: colors.error.solid, opacity: 0.9 },
                    }}
                  >
                    Reject
                  </CustomButton>
                </span>
              </Tooltip>
            )}

            {show.restore && (
              <Tooltip title={reviewDeniedReason}>
                <span>
                  <CustomButton
                    fullWidth
                    disabled={!canReview}
                    onClick={handlers.openRestoreModal}
                    endIcon={<RestoreIcon width={16} height={16} />}
                    sx={{
                      height: 40,
                      borderRadius: '8px',
                      fontWeight: 500,
                      background: colors.warning.solid,
                      color: '#0A0A0A',
                      '&:hover': { background: colors.warning.solid, opacity: 0.9 },
                    }}
                  >
                    Restore
                  </CustomButton>
                </span>
              </Tooltip>
            )}

            {show.unblock && (
              <Tooltip title={reviewDeniedReason}>
                <span>
                  <CustomButton
                    fullWidth
                    disabled={!canReview}
                    onClick={handlers.openUnblockModal}
                    endIcon={<UnblockIcon color="#0A0A0A" />}
                    sx={{
                      height: 40,
                      borderRadius: '8px',
                      fontWeight: 500,
                      background: colors.warning.solid,
                      color: '#0A0A0A',
                      '&:hover': { background: colors.warning.solid, opacity: 0.9 },
                    }}
                  >
                    Unblock
                  </CustomButton>
                </span>
              </Tooltip>
            )}

            {show.edit && (
              <CustomButton
                fullWidth
                variant="outlined"
                onClick={handleReview}
                endIcon={<ManageIcon width={16} height={16} />}
                sx={{
                  height: 40,
                  borderRadius: '8px',
                  fontWeight: 500,
                  borderColor: 'rgba(243,243,243,0.2)',
                  color: colors.text.primary,
                }}
              >
                Review
              </CustomButton>
            )}
          </ActionRow>
        )}

        {bottomRowVisible && (
          <ActionRow>
            {show.contact && (
              <CustomButton
                fullWidth
                variant="outlined"
                onClick={handlers.contact}
                endIcon={<SmsIcon width={16} height={16} />}
                sx={{
                  height: 40,
                  borderRadius: '8px',
                  fontWeight: 500,
                  borderColor: 'rgba(243,243,243,0.2)',
                  color: colors.text.primary,
                }}
              >
                Contact
              </CustomButton>
            )}

            {show.block && (
              <Tooltip title={reviewDeniedReason}>
                <span>
                  <CustomButton
                    fullWidth
                    disabled={!canReview}
                    onClick={handlers.openBlockModal}
                    endIcon={<LockIcon width={16} height={16} />}
                    sx={{
                      height: 40,
                      borderRadius: '8px',
                      fontWeight: 500,
                      color: colors.text.error,
                      background: `${colors.error.solid}1A`,
                      '&:hover': { background: `${colors.error.solid}33` },
                    }}
                  >
                    Block
                  </CustomButton>
                </span>
              </Tooltip>
            )}

            {show.delete && (
              <Tooltip title={deleteDeniedReason}>
                <span>
                  <CustomButton
                    fullWidth
                    disabled={!canDelete}
                    onClick={handlers.openDeleteModal}
                    startIcon={<TrashIcon width={16} height={16} />}
                    sx={{
                      height: 40,
                      borderRadius: '8px',
                      fontWeight: 500,
                      color: colors.text.error,
                      background: `${colors.error.solid}1A`,
                      '&:hover': { background: `${colors.error.solid}33` },
                    }}
                  >
                    Delete
                  </CustomButton>
                </span>
              </Tooltip>
            )}
          </ActionRow>
        )}
      </ActionsCol>
    </GalleryItemContainer>
  );
};
